import { ApiResponse } from '../types/api';

export interface LogEntry<LogPayload = unknown, LogResponse = unknown, LogData = unknown> {
    page: string;
    method?: string;
    url?: string;
    payload?: LogPayload;
    start?: Date;
    end?: Date;
    duration?: number;
    status?: number;
    response?: LogResponse;
    data?: LogData;
    bypass?: boolean;
}

export interface DatabaseLogEntry extends Pick<LogEntry, 'page' | 'url'> {
    sql: string;
    duration: number;
}

export interface RequestLogEntry extends Pick<LogEntry, 'page' | 'url'> {
    payload: string;
    response: string;
    duration: number;
}

export interface WebhookLogEntry extends Pick<LogEntry, 'page' | 'url'> {
    nBrandID: string;
    nEventTypeID: number;
    vars: string[];
    duration: number;
}

export const logApiRequest = ({
    method,
    url,
    payload,
    logs = [],
    notify
}: {
    method: string;
    url: string;
    payload: unknown;
    logs?: LogEntry[];
    notify?: (log: LogEntry[]) => void;
}) => {
    const event: LogEntry = {
        page: window.location.pathname,
        method,
        url,
        payload,
        start: new Date()
    };

    logs.unshift(event);

    if (logs.length > 100) {
        logs.pop();
    }

    if (logs.length > 0) {
        notify?.(logs);
    }

    return event;
};

export const updateApiLogEntry = ({
    event,
    response,
    data,
    logs = [],
    notify
}: {
    event: LogEntry;
    response: ApiResponse;
    data: unknown;
    logs?: LogEntry[];
    notify?: (logs: LogEntry[]) => void;
}) => {
    const updatedEvent = { ...event };

    updatedEvent.end = new Date();
    updatedEvent.duration = updatedEvent.end.getTime() - (updatedEvent?.start?.getTime() ?? 0);
    updatedEvent.status = response.status;
    updatedEvent.response = response;
    updatedEvent.data = data;
    updatedEvent.bypass = Boolean(response?.bypass);

    const updatedLogs = [...logs].map(d => {
        if (d.url === event.url && d.method === event.method) {
            return updatedEvent;
        }
        return d;
    });

    if (updatedLogs.length > 0) {
        notify?.(updatedLogs);
    }

    return updatedLogs;
};

export const logOperations = <T = unknown, U = unknown>({
    path,
    operations = [],
    logs = [],
    notify
}: {
    path: string;
    operations?: U[];
    logs?: T[];
    notify?: (logs: T[]) => void;
}): T[] => {
    const newEntries = (operations?.map(operation => ({
        page: window.location.pathname,
        url: path,
        ...operation
    })) ?? []) as T[];
    const updatedLogs = [...newEntries.reverse(), ...logs];

    if (updatedLogs.length > 100) {
        updatedLogs.length = 100;
    }

    if (updatedLogs.length > 0) {
        notify?.(updatedLogs);
    }

    return updatedLogs;
};
